import { useMutation, useQueryClient } from '@tanstack/react-query';
import { submitResponse } from '@/api/survey';
import { toast } from '@/hooks/use-toast';
import { useNoGuard } from '@/hooks/useNoGuard';

export function useSurveyResponse() {
  const noGuard = useNoGuard();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userModuleId, questionId, response }: {
      userModuleId: number;
      questionId: number;
      response: number | string;
    }) => {
      console.log('useSurveyResponse mutationFn called with:', {
        userModuleId,
        questionId,
        response,
        responseType: typeof response,
        noGuard
      });
      
      // In noguard mode, just mock success
      if (noGuard) {
        console.log('[NoGuard Mode] Mock saving survey response');
        return Promise.resolve({
          id: Math.random(),
          userModuleId,
          questionId,
          response: response,
          createdAt: new Date().toISOString()
        });
      }
      
      console.log('Calling submitResponse API with:', {
        userModuleId,
        questionId,
        response,
        responseType: typeof response
      });
      
      return submitResponse(userModuleId, questionId, response);
    },
    onSuccess: (data, variables) => {
      console.log('Survey response submitted successfully:', data);
      // Invalidate survey responses cache to refetch latest data
      queryClient.invalidateQueries({ 
        queryKey: ['survey-responses', variables.userModuleId] 
      });
    },
    onError: (error: any) => {
      console.error('Failed to submit survey response:', error);
      console.error('Full error object:', {
        message: error.message,
        response: error.response,
        data: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      
      // More specific error messages
      let errorMessage = "Failed to save your response. Please try again.";
      if (error.response?.status === 404) {
        errorMessage = "Survey question not found. Please refresh the page.";
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.detail || "Invalid response format.";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  });
}